import * as couponService from '@cart-services/core/couponService'
import { useFpCampaignCartStore } from '@cart-services/hooks/useFpCampaignCartStore'
import { useFpCampaignCouponService } from '@cart-services/hooks/useFpCampaignCouponService'
import { type GiveawayValue } from '@cart-services/types'
import { useDeepCompareEffect } from '@dni-hooks/use-deep-compare-effect'
import { zodResolver } from '@hookform/resolvers/zod'
import { type ECommerceProduct } from '@hooks-api/types/dataModel/products'
import { type PropsWithChildren } from 'react'
import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form'
import { zodCampaignCartSchema, type CartFormContext } from '../../zods/zodCampaignCartSchema'
import { useContextCampaignCart } from './CampaignCartProvider'
export const CART_FORM_ID = 'cart-form'

export interface MerchantValue {
  label: string
  value: number | null
}
export interface CartFormValues {
  regularGiveaways: (ECommerceProduct['id'] | undefined)[]
  giveaways?: GiveawayValue[]
}

export interface CartFormProviderProps {
  onSubmit: SubmitHandler<CartFormValues>
}

export const CartFormProvider = (props: PropsWithChildren<CartFormProviderProps>) => {
  const { children, onSubmit } = props
  const { campaignId, countryCode, campaignCode } = useContextCampaignCart()
  const [cartValues] = useFpCampaignCartStore(campaignId)
  const { regularGiveaways, selectedCouponGiveawayMap, appliedGiveaways } = useFpCampaignCouponService(
    campaignId,
    countryCode,
    campaignCode,
  )
  const methods = useForm<CartFormValues, CartFormContext>({
    resolver: zodResolver(zodCampaignCartSchema({ regularGiveaways, selectedCouponGiveawayMap, isGiveawayDispatch: !!cartValues.isGiveawayDispatch })),
    mode: 'onChange',
    defaultValues: {
      regularGiveaways: [],
      giveaways: [],
    },
  })

  useDeepCompareEffect(() => {
    if (regularGiveaways) {
      const criterion = regularGiveaways.flatMap(regularGiveaway => regularGiveaway.criterions.map(criterion => criterion))
      const regularGiveawaysArray = criterion.map(criterion => couponService.getRegularGiveawaySelectProps(criterion, selectedCouponGiveawayMap)).map(props => props.value?.product_id)
      methods.setValue('regularGiveaways', regularGiveawaysArray, { shouldDirty: false })
    }

    if (appliedGiveaways) {
      const giveaways: GiveawayValue[] = appliedGiveaways.map((giveaway) => {
        const { coupon, ...criterion } = giveaway
        const {
          couponCriterionId,
          totalSelected,
          accumulatedGiftSelectionCount,
          giveawayValuesWithValidateStock,
          isGiveAll,
          criterionCount,
        } = couponService.getGiveawaySelectProps(coupon, criterion, selectedCouponGiveawayMap)
        return {
          couponCriterionId,
          totalSelected,
          accumulatedGiftSelectionCount,
          giveawayValuesWithValidateStock,
          isGiveAll,
          criterionCount,
        }
      })
      methods.setValue('giveaways', giveaways, { shouldDirty: false })
    }
  }, [regularGiveaways, selectedCouponGiveawayMap, appliedGiveaways, methods])

  return (
    <FormProvider {...methods}>
      <form id={CART_FORM_ID} onSubmit={methods.handleSubmit(onSubmit)} />
      {children}
    </FormProvider>
  )
}
